/* eslint-disable camelcase */
import { NativeSelect } from '@mantine/core'
import clsx from 'clsx'
import { useState } from 'react'

import { Button } from 'src/components/Button/Button'
import { Text } from 'src/components/Text/Text'
import { useLocale } from 'src/contexts/LocaleContext'

import styles from './GiftSubscriptionCard.module.scss'

import type { SubscriptionPlan, SubscriptionPrice } from 'src/types/Subscription'

interface GiftSubscriptionCardProps {
  prices: SubscriptionPrice[]
  plans: SubscriptionPlan[]
}

export const GiftSubscriptionCard = ({ prices, plans }: GiftSubscriptionCardProps) => {
  const { locale } = useLocale()

  const [defaultPrice] = prices
  const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined)

  const formatPrice = (priceNumeric: number) =>
    (priceNumeric / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

  const selectElements = plans.map((plan) => ({ value: plan.id, label: plan.external_name }))
  const selectedPrice = prices.find((price) => price.itemId === selectedOption) || defaultPrice

  const {
    price: priceNumeric,
    periodMonths,
    priceCategoryHighlighted,
    priceCategoryAlias,
    priceCategoryInfoText,
    priceCategoryCtaChipText,
  } = selectedPrice

  return (
    <div className={clsx(styles.card, priceCategoryHighlighted && styles.big)}>
      {priceCategoryCtaChipText && (
        <Text x-small className={clsx(styles.chip, priceCategoryHighlighted && styles.big)}>
          {priceCategoryCtaChipText}
        </Text>
      )}
      <Text className={clsx(styles.periodMonths, priceCategoryHighlighted && styles.big)}>{periodMonths} Monate</Text>
      <Text className={clsx(styles.priceAlias, priceCategoryHighlighted && styles.big)}>{priceCategoryAlias}</Text>
      <Text variant="p" className={clsx(styles.categoryInfo, priceCategoryHighlighted && styles.big)}>
        {priceCategoryInfoText}
      </Text>
      <div className={styles.priceAndMonths}>
        <Text className={styles.price}>{formatPrice(priceNumeric)}</Text>
        <Text x-small className={styles.monthsInfo}>
          für <b>{periodMonths}</b> <b>Monate</b> besten Kaffee
        </Text>
      </div>
      <div className={styles.buttonsContainer}>
        <NativeSelect
          data={[{ value: '', label: 'Zubereitung wählen', disabled: true }, ...selectElements]}
          defaultValue=""
          value={selectedOption}
          onChange={(event) => setSelectedOption(event.target.value)}
          className={clsx(priceCategoryHighlighted && styles.invertedSelect)}
        />

        {/* all buttons need to be initialized for the chargebee drop-in checkout, so we just toggle visibility */}
        {prices.map((price: SubscriptionPrice) => (
          <div
            key={price.id}
            className={styles.priceBox}
            style={{ display: price.id === selectedPrice.id ? 'flex' : 'none' }}
          >
            <Button data-cb-type="checkout" data-cb-item-0={price.id} className={styles.button} fullWidth>
              Jetzt bestellen
            </Button>
          </div>
        ))}
      </div>
      {priceCategoryHighlighted && <div className={styles.rainbow} />}
    </div>
  )
}
